import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Stack, Group, Card, Table, Text, Button, Modal, TextInput, Select, Badge,
  Center, Loader, Alert, ActionIcon, Box,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Plus, Play, Trash2, Check, Calendar, Pencil } from 'lucide-react';

import { epochsApi, type Epoch, type MvpStrategy } from '../api';

const STRATEGY_LABELS: Record<MvpStrategy, string> = {
  none: 'Bez MVP',
  classic: 'Klasické MVP',
  per_group: 'Vítěz skupiny',
};

const STRATEGY_OPTIONS = (Object.keys(STRATEGY_LABELS) as MvpStrategy[]).map((value) => ({
  value,
  label: STRATEGY_LABELS[value],
}));

export default function EpochsPage() {
  const qc = useQueryClient();
  const isMobile = useMediaQuery('(max-width: 48em)');

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Epoch | null>(null);
  const [name, setName] = useState('');
  const [strategy, setStrategy] = useState<MvpStrategy>('classic');
  const [nameError, setNameError] = useState<string | null>(null);

  const epochs = useQuery({ queryKey: ['epochs'], queryFn: epochsApi.getAll });

  const fail = (title: string) => (e: any) =>
    notifications.show({ title, message: e?.response?.data?.error || e.message, color: 'red' });

  const save = useMutation({
    mutationFn: () =>
      editing
        ? epochsApi.update(editing.id, { name: name.trim(), mvp_strategy: strategy })
        : epochsApi.create(name.trim(), strategy),
    onSuccess: () => {
      notifications.show({ message: editing ? 'Epocha uložena' : 'Epocha založena', color: 'forest' });
      setOpen(false);
      qc.invalidateQueries();
    },
    onError: fail('Uložení se nepovedlo'),
  });

  const activate = useMutation({
    mutationFn: epochsApi.activate,
    onSuccess: () => {
      notifications.show({ message: 'Epocha aktivována', color: 'forest' });
      // Epocha mění všechna data včetně statistik, takže se zneplatní celá cache.
      qc.invalidateQueries();
    },
    onError: fail('Aktivace se nepovedla'),
  });

  const remove = useMutation({
    mutationFn: epochsApi.delete,
    onSuccess: () => { notifications.show({ message: 'Epocha smazána' }); qc.invalidateQueries(); },
    onError: fail('Smazání se nepovedlo'),
  });

  const openNew = () => { setEditing(null); setName(''); setStrategy('classic'); setNameError(null); setOpen(true); };
  const openEdit = (e: Epoch) => {
    setEditing(e); setName(e.name); setStrategy(e.mvp_strategy); setNameError(null); setOpen(true);
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) { setNameError('Doplň název'); return; }
    save.mutate();
  };

  const confirmDelete = (e: Epoch) => {
    if (window.confirm(
      `Smazat epochu ${e.name}? Smažou se i všichni její členové, skupiny a hry. Tohle nejde vrátit.`
    )) remove.mutate(e.id);
  };

  if (epochs.isLoading) return <Center h={240}><Loader /></Center>;
  if (epochs.error) {
    return <Alert color="red" title="Epochy se nepodařilo načíst">{(epochs.error as any)?.message}</Alert>;
  }

  const list = epochs.data ?? [];

  const rowActions = (e: Epoch) => (
    <Group gap={2} justify="flex-end" wrap="nowrap">
      {e.is_active !== 1 && (
        <Button
          size="compact-xs" variant="default" leftSection={<Play size={12} />}
          loading={activate.isPending && activate.variables === e.id}
          onClick={() => activate.mutate(e.id)}
        >
          Aktivovat
        </Button>
      )}
      <ActionIcon aria-label={`Upravit ${e.name}`} onClick={() => openEdit(e)}>
        <Pencil size={14} />
      </ActionIcon>
      <ActionIcon
        aria-label={`Smazat ${e.name}`} color="red"
        disabled={e.is_active === 1}
        onClick={() => confirmDelete(e)}
      >
        <Trash2 size={14} />
      </ActionIcon>
    </Group>
  );

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text c="dimmed" fz="sm">
          Aktivní je vždy nejvýš jedna epocha. Mobilní zařízení se hlásí k té aktivní.
        </Text>
        <Button leftSection={<Plus size={14} />} onClick={openNew}>Nová epocha</Button>
      </Group>

      <Card padding={0}>
        {list.length === 0 ? (
          <Stack align="center" py="xl" gap={4}>
            <Calendar size={22} color="var(--mantine-color-dimmed)" />
            <Text fw={600}>Zatím tu není žádná epocha</Text>
            <Text c="dimmed" fz="sm">Bez ní nejdou zakládat členové ani hry.</Text>
          </Stack>
        ) : isMobile ? (
          <Box>
            {list.map((e) => (
              <Group key={e.id} p="sm" gap="sm" wrap="nowrap"
                style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
                <Calendar
                  size={16}
                  color={e.is_active === 1 ? 'var(--mantine-primary-color-filled)' : 'var(--mantine-color-dimmed)'}
                  style={{ flex: '0 0 16px' }}
                />
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Group gap={6} wrap="nowrap">
                    <Text fw={650} fz="sm" truncate>{e.name}</Text>
                    {e.is_active === 1 && <Badge color="forest" size="xs">aktivní</Badge>}
                  </Group>
                  <Text c="dimmed" fz="xs">{STRATEGY_LABELS[e.mvp_strategy] ?? e.mvp_strategy}</Text>
                </Box>
                {rowActions(e)}
              </Group>
            ))}
          </Box>
        ) : (
          <Table.ScrollContainer minWidth={560}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Epocha</Table.Th>
                  <Table.Th w={160}>MVP hlasování</Table.Th>
                  <Table.Th w={132}>Stav</Table.Th>
                  <Table.Th w={190} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {list.map((e) => (
                  <Table.Tr key={e.id}>
                    <Table.Td>
                      <Group gap={8} wrap="nowrap">
                        <Calendar
                          size={15}
                          color={e.is_active === 1 ? 'var(--mantine-primary-color-filled)' : 'var(--mantine-color-dimmed)'}
                        />
                        <Text fw={600} fz="sm">{e.name}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td><Text fz="sm" c="dimmed">{STRATEGY_LABELS[e.mvp_strategy] ?? e.mvp_strategy}</Text></Table.Td>
                    <Table.Td>
                      {e.is_active === 1
                        ? <Badge color="forest" leftSection={<Check size={11} />}>aktivní</Badge>
                        : <Badge color="gray">neaktivní</Badge>}
                    </Table.Td>
                    <Table.Td>{rowActions(e)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>

      <Text c="dimmed" fz="xs">
        Aktivní epochu nejde smazat — nejdřív aktivuj jinou.
      </Text>

      <Modal opened={open} onClose={() => setOpen(false)} title={editing ? 'Upravit epochu' : 'Nová epocha'}>
        <form onSubmit={submit}>
          <Stack gap="sm">
            <TextInput
              label="Název" placeholder="např. Letní tábor 2026" value={name} error={nameError}
              onChange={(ev) => { setName(ev.currentTarget.value); setNameError(null); }}
              data-autofocus
            />
            <Select
              label="MVP hlasování" data={STRATEGY_OPTIONS} value={strategy}
              onChange={(v) => setStrategy((v as MvpStrategy) ?? 'classic')}
              allowDeselect={false}
            />
            <Group justify="flex-end" mt="xs">
              <Button variant="default" type="button" onClick={() => setOpen(false)}>Zrušit</Button>
              <Button type="submit" leftSection={<Check size={14} />} loading={save.isPending}>
                {editing ? 'Uložit změny' : 'Založit epochu'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
